// Servicio de horarios unificado — replica los endpoints que usa la app Flutter
// (`flutterUXS/lib/backend/api_requests/api_calls.dart`):
//
//   * GETNewProfessionalScheduleCall              → por doctora (week schedule unified)
//   * GETDoctoresAPartirDeUnaFechaParaConsultaCall → por fecha (schedule_by_date unified)
//   * POSTObtenerProfesionalesPorCategoriaCall    → por condición (professionals_by_category)
//   * POSTCrearUnaReunionConUnDoctorIncluidaCall  → booking final
//
// Todos los endpoints pasan `unified_view: true, source: "daisies"` igual que el
// nuevo flow de la app, lo cual hace que el backend devuelva slots atómicos con
// `hour_start/hour_stop`, `booking_mode`, `schedule_type`, etc.
//
// La granularidad atómica se respeta con `slot_duration_minutes` y la duración
// final se agrupa en cliente con `groupSlotsByDuration`.

import {
  parseWeekScheduleResponse,
  groupSlotsByDuration,
} from './scheduleModels';
import { isPhoneNotVerifiedResponse } from './authVerificationService';
import {
  filterTestProfessionals,
  noteTestProfessionalsFromUserScopedResponse,
} from '../utils/testProfessional';

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const PUBLIC_API_URL = 'https://odoo.core.daisies.mx/marketing-manager/import-data';
const ACCESS_TOKEN = process.env.REACT_APP_ACCESS_TOKEN;
const ACCOUNT_ID = parseInt(process.env.REACT_APP_ACCOUNT_ID, 10) || 1;

// user_id placeholder para llamadas anónimas (idéntico al patrón que ya usa
// doctorService.js). Algunos endpoints exigen un int aunque el usuario no esté
// logueado todavía — el backend lo trata como "guest".
const GUEST_USER_ID = 71403;

const tzDefault = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Mexico_City';
  } catch (e) {
    return 'America/Mexico_City';
  }
};

const formatYmd = (date) => {
  if (date instanceof Date) return date.toISOString().split('T')[0];
  if (typeof date === 'string' && date.length >= 10) return date.slice(0, 10);
  return new Date().toISOString().split('T')[0];
};

const post = async (url, payload) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  return response.json();
};

// =================== POR DOCTORA (week schedule unified) ===================
// Réplica de `GETNewProfessionalScheduleCall`.
//
// `auth_token` puede estar vacío para usuarios no logueados (browse libre),
// igual que hace doctorService actual.
export const getProfessionalWeekSchedule = async ({
  doctorId,
  userId,
  durationId,
  dateStart,
  dateEnd,
  tz,
  authToken,
  bannerId = 0,
}) => {
  if (!doctorId || !durationId) {
    return { success: false, slotDurationMinutes: 0, days: [], hasAvailability: false };
  }
  const start = dateStart ? formatYmd(dateStart) : formatYmd(new Date());
  const end = dateEnd ? formatYmd(dateEnd) : (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return formatYmd(d);
  })();

  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken || '',
      res_model: 'dais.consultation',
      res_method: 'get_professional_schedule_today_to_three_days_after',
      res_params: {
        doctor_id: Number(doctorId),
        user_id: Number(userId) || GUEST_USER_ID,
        date_start: start,
        date_end: end,
        duration_id: Number(durationId),
        tz: tz || tzDefault(),
        ignore_inscription_membership: false,
        banner_id: bannerId || 0,
        unified_view: true,
        source: 'daisies',
      },
    },
  };

  try {
    const data = await post(API_URL, payload);
    const parsed = parseWeekScheduleResponse(data?.result);
    if (parsed.success && (parsed.days.length > 0 || parsed.hasAvailability)) return parsed;

    // Fallback: el endpoint unified puede no aceptar usuarios sin auth_token.
    // Para conservar el "explorar libre" de la web caemos al endpoint legacy
    // público (get_professional_schedule_today_for_externals) y normalizamos
    // la respuesta al mismo shape de WeekSchedule.
    const legacyPayload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: ACCOUNT_ID,
        auth_token: authToken || '',
        res_model: 'dais.consultation',
        res_method: 'get_professional_schedule_today_for_externals',
        res_params: {
          doctor_id: Number(doctorId),
          date_start: start,
          date_end: end,
          duration_id: Number(durationId),
          tz: tz || tzDefault(),
        },
      },
    };
    const legacyData = await post(API_URL, legacyPayload);
    return parseWeekScheduleResponse(legacyData?.result);
  } catch (err) {
    console.warn('[scheduleService.getProfessionalWeekSchedule] error', err);
    return { success: false, slotDurationMinutes: 0, days: [], hasAvailability: false };
  }
};

// =================== POR FECHA (schedule_by_date unified) ===================
// Réplica de `GETDoctoresAPartirDeUnaFechaParaConsultaCall`.
// El backend devuelve slots con `professional_ids` por slot.
export const getScheduleByDate = async ({
  specialtyId,
  durationId,
  userId,
  date,
  isExtra = false,
  authToken,
  consultationReason = '',
  reminder = true,
}) => {
  if (!specialtyId || !durationId || !date) {
    return { success: false, slotDurationMinutes: 0, days: [], hasAvailability: false };
  }

  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken || '',
      res_model: 'dais.consultation',
      res_method: 'create_include_consultation_by_user_v2',
      res_id: 1,
      res_params: {
        specialty_id: Number(specialtyId),
        duration_id: Number(durationId),
        user_id: Number(userId) || GUEST_USER_ID,
        date: formatYmd(date),
        reason: consultationReason || '',
        reminder: !!reminder,
        is_extra: !!isExtra,
        unified_view: true,
        source: 'daisies',
      },
    },
  };

  try {
    const data = await post(API_URL, payload);
    // Endpoint gateado por user_type (vía get_professional_ids): si la
    // respuesta de un usuario real autenticado trae doctoras de prueba, el
    // backend autorizó verlas. Persistir ANTES de parsear para que esta misma
    // respuesta no se filtre en parseDoctorSlot.
    if (authToken && Number(userId) && Number(userId) !== GUEST_USER_ID) {
      noteTestProfessionalsFromUserScopedResponse(data?.result);
    }
    return parseWeekScheduleResponse(data?.result);
  } catch (err) {
    console.warn('[scheduleService.getScheduleByDate] error', err);
    return { success: false, slotDurationMinutes: 0, days: [], hasAvailability: false };
  }
};

// Fallback para usuarios anónimos: el endpoint unified suele requerir un user_id
// real. Si por alguna razón falla, caemos al endpoint público v2_public con los
// mismos flags (`unified_view: true, source: "daisies"`) para que el shape de
// respuesta coincida (slots con `booking_mode`, `professional_ids` enriquecido).
export const getScheduleByDateWithFallback = async (args) => {
  const result = await getScheduleByDate(args);
  // Hay días con slots (aunque algunos estén locked), consideramos éxito.
  const hasAnyData = result?.success && (result.days?.length > 0 || result.hasAvailability);
  if (hasAnyData) return result;

  // Fallback al endpoint público v2 que no requiere auth.
  try {
    const payload = {
      params: {
        access_token: ACCESS_TOKEN,
        account_id: ACCOUNT_ID,
        res_id: 1,
        res_model: 'dais.consultation',
        res_method: 'create_include_consultation_by_user_v2_public',
        res_params: {
          specialty_id: Number(args.specialtyId),
          duration_id: Number(args.durationId),
          date: formatYmd(args.date),
          is_extra: !!args.isExtra,
          tz: args.tz || tzDefault(),
          // Pedimos al backend la vista unificada también en el endpoint público
          // — si lo soporta devuelve booking_mode/schedule_type; si lo ignora
          // recibimos slots con shape legacy (que parseDoctorSlot tolera).
          unified_view: true,
          source: 'daisies',
        },
      },
    };
    const data = await post(PUBLIC_API_URL, payload);
    return parseWeekScheduleResponse(data?.result);
  } catch (err) {
    console.warn('[scheduleService.getScheduleByDateWithFallback] fallback failed', err);
    return result;
  }
};

// =================== POR CONDICIÓN (professionals_by_category) ===================
// Réplica de `POSTObtenerProfesionalesPorCategoriaCall`.
// Devuelve la lista de profesionales que tratan las condiciones seleccionadas.
export const getProfessionalsByCategories = async ({
  specialtyId,
  categoryIds = [],
  extra = false,
  userId,
  authToken,
}) => {
  if (!specialtyId) return { success: false, professionals: [] };

  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken || '',
      res_model: 'dais.professional',
      res_method: 'process_get_professional_by_categories',
      res_params: {
        user_id: Number(userId) || GUEST_USER_ID,
        specialty_id: Number(specialtyId),
        extra: !!extra,
        category_ids: Array.isArray(categoryIds) ? categoryIds.map(Number) : [],
      },
    },
  };

  try {
    const data = await post(API_URL, payload);
    const success = !!data?.result?.success;
    const raw = Array.isArray(data?.result?.data) ? data.result.data : [];
    // Endpoint gateado por user_type (vía get_professional_ids): ver
    // noteTestProfessionalsFromUserScopedResponse. Debe correr antes del filtro.
    if (authToken && Number(userId) && Number(userId) !== GUEST_USER_ID) {
      noteTestProfessionalsFromUserScopedResponse(raw);
    }
    // Doctoras de prueba: ocultas salvo localhost/usuario interno/acceso backend.
    const professionals = filterTestProfessionals(raw);
    return { success, professionals };
  } catch (err) {
    console.warn('[scheduleService.getProfessionalsByCategories] error', err);
    return { success: false, professionals: [] };
  }
};

// Obtiene la info enriquecida de una especialidad incluyendo sus duraciones
// con los minutos correctos. Réplica de `dais.specialty.get_model_data` que
// usa la app Flutter — los campaign endpoints suelen devolver sólo el nombre
// sin el campo `duration`, lo cual hace que el picker no muestre los minutos.
export const getSpecialtyData = async ({ specialtyId, authToken }) => {
  if (!specialtyId) return null;
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken || '',
      res_model: 'dais.specialty',
      res_method: 'get_model_data',
      res_id: Number(specialtyId),
      res_params: {},
    },
  };
  try {
    const data = await post(API_URL, payload);
    const result = data?.result;
    // El backend puede devolver `{success: true, data: {...}}` o `data` directo.
    return result?.data || result || null;
  } catch (err) {
    console.warn('[scheduleService.getSpecialtyData] error', err);
    return null;
  }
};

// Obtiene las categorías/condiciones disponibles para una especialidad.
// Endpoint usado por la app Flutter en la pantalla de "agendar por condición".
export const getCategoriesBySpecialty = async ({ specialtyId }) => {
  if (!specialtyId) return [];
  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      res_model: 'dais.professional.category',
      res_method: 'get_categories_by_specialty',
      res_params: {
        specialty_id: Number(specialtyId),
      },
    },
  };
  try {
    const data = await post(API_URL, payload);
    const list = Array.isArray(data?.result?.data) ? data.result.data
      : (Array.isArray(data?.result) ? data.result : []);
    return list;
  } catch (err) {
    console.warn('[scheduleService.getCategoriesBySpecialty] error', err);
    return [];
  }
};

// =================== VALIDAR SLOT (pre-booking) ===================
// Réplica de `POSTValidarUsuariaAplicaParaAgendarConsultaIncluidaCall`.
// La app Flutter llama esto justo después de tocar el slot para saber qué
// flujo de pago aplica (membresía / sin minutos / sin inscripción / pago extra).
//
// El backend devuelve flags que el cliente convierte en un "UserOrigin" para
// decidir qué sheet mostrar:
//   - success === true → puede agendar como incluida ya
//   - without_membership / without_inscription → necesita comprar membresía o pagar extra
//   - without_minutes → tiene membresía pero sin minutos → comprar minutos / pagar extra
//   - membership_ends_before → la membresía vence antes de la cita
//   - preferential_requires_membership → slot preferencial sin membresía
export const validateScheduleInclude = async ({
  authToken,
  doctorId,
  userId,
  date,
  durationId,
  scheduleId,
}) => {
  if (!authToken || !userId || !doctorId) {
    return { success: false, error: 'Missing auth/user/doctor params for validation.' };
  }

  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_model: 'dais.consultation',
      res_method: 'validate_schedule_include_consultation',
      res_params: {
        doctor_id: Number(doctorId),
        user_id: Number(userId),
        date: typeof date === 'string' ? date : formatYmd(date),
        duration_id: Number(durationId),
        schedule_id: scheduleId != null ? Number(scheduleId) : null,
      },
    },
  };

  try {
    const data = await post(API_URL, payload);
    const result = data?.result || {};
    return {
      success: !!result.success,
      withoutMembership: !!result.without_membership,
      withoutInscription: !!result.without_inscription,
      withoutMinutes: !!result.without_minutes,
      membershipEndsBefore: !!result.membership_ends_before,
      preferentialRequiresMembership: !!result.preferential_requires_membership,
      message: result.message || '',
      raw: result,
    };
  } catch (err) {
    console.warn('[scheduleService.validateScheduleInclude] error', err);
    return { success: false, error: err.message };
  }
};

// =================== CREAR CONSULTA (booking final) ===================
// Réplica de `POSTCrearUnaReunionConUnDoctorIncluidaCall`.
// Detecta automáticamente si la respuesta es on-demand (la doctora debe
// confirmar) leyendo `requires_doctor_confirmation` / `state`.
export const createConsultation = async ({
  authToken,
  doctorId,
  userId,
  date,
  durationId,
  scheduleId,
  reason = '',
  reminder = true,
  tz,
  parentId = null,
  consultationReasonId = null,
  specialtyId = null,
}) => {
  if (!authToken || !userId || !doctorId || !durationId) {
    return { success: false, error: 'Missing required booking params (auth, user, doctor, duration).' };
  }

  const payload = {
    params: {
      access_token: ACCESS_TOKEN,
      account_id: ACCOUNT_ID,
      auth_token: authToken,
      res_id: 1,
      res_model: 'dais.consultation',
      res_method: 'create_include_consultation_by_user',
      res_params: {
        doctor_id: Number(doctorId),
        user_id: Number(userId),
        date: typeof date === 'string' ? date : formatYmd(date),
        duration_id: Number(durationId),
        schedule_id: scheduleId != null ? Number(scheduleId) : null,
        reason: reason || '',
        reminder: !!reminder,
        tz: tz || tzDefault(),
        parent_id: parentId,
        consultation_reason_id: consultationReasonId,
        specialty_id: specialtyId != null ? Number(specialtyId) : null,
      },
    },
  };

  try {
    const data = await post(API_URL, payload);
    const result = data?.result || {};
    const success = !!result.success;
    const onDemand =
      result?.data?.requires_doctor_confirmation === true ||
      result?.data?.state === 'pending_doctor_confirmation';
    // Gate V2: phone_verified == false
    const phoneNotVerified = !success && isPhoneNotVerifiedResponse(data);
    return {
      success,
      phoneNotVerified,
      message: result?.message || '',
      data: result?.data || null,
      onDemand,
      deadline: result?.data?.doctor_confirmation_deadline || null,
      raw: data,
    };
  } catch (err) {
    console.warn('[scheduleService.createConsultation] error', err);
    return { success: false, error: err.message };
  }
};

// =================== HELPERS PÚBLICOS ===================
// Combina WeekSchedule + groupSlotsByDuration en una sola llamada.
// Útil para callers que ya conocen la duración objetivo.
export const fetchAndGroupByDoctor = async (args, { targetDurationMinutes }) => {
  const week = await getProfessionalWeekSchedule(args);
  return {
    ...week,
    days: week.days.map(day => ({
      ...day,
      bookingSlots: groupSlotsByDuration({
        slots: day.slots,
        targetDurationMinutes,
        atomicSlotMinutes: week.slotDurationMinutes,
      }),
    })),
  };
};

export const fetchAndGroupByDate = async (args, { targetDurationMinutes }) => {
  const week = await getScheduleByDateWithFallback(args);
  return {
    ...week,
    days: week.days.map(day => ({
      ...day,
      bookingSlots: groupSlotsByDuration({
        slots: day.slots,
        targetDurationMinutes,
        atomicSlotMinutes: week.slotDurationMinutes,
      }),
    })),
  };
};

/**
 * Obtiene los slots disponibles de una doctora en un rango de fechas.
 * Wrapper sobre getProfessionalWeekSchedule + groupSlotsByDuration.
 * Devuelve un array flat para uso de MatchConfirmationPage:
 *   [{ date, hour, init, booking_mode, is_locked, available, slotId, end, ... }]
 */
export const getDoctorSchedule = async (doctorId, dateStart, dateEnd, durationId, tz = 'America/Mexico_City') => {
  try {
    const week = await getProfessionalWeekSchedule({
      doctorId,
      durationId,
      dateStart,
      dateEnd,
      tz,
    });
    if (!week?.success || !Array.isArray(week.days)) return [];

    const targetMin = Number(week.slotDurationMinutes) || 0;
    const atomicMin = Number(week.slotDurationMinutes) || targetMin;

    const out = [];
    for (const day of week.days) {
      const bookingSlots = groupSlotsByDuration({
        slots: day.slots || [],
        targetDurationMinutes: targetMin || 60,
        atomicSlotMinutes: atomicMin || 60,
      });
      for (const b of bookingSlots) {
        if (b.isOccupied) continue;
        out.push({
          date: day.date,
          hour: b.hourStart,
          init: b.hourStart,
          end: b.hourStop,
          booking_mode: b.bookingMode,
          is_locked: !!b.isLocked,
          is_extra: !!b.isExtra,
          available: true,
          slotId: b.slotId,
          professional_ids: b.professionalIds,
        });
      }
    }
    return out;
  } catch (err) {
    console.warn('[getDoctorSchedule] error:', err);
    return [];
  }
};

const scheduleService = {
  getProfessionalWeekSchedule,
  getScheduleByDate,
  getScheduleByDateWithFallback,
  getProfessionalsByCategories,
  getCategoriesBySpecialty,
  getSpecialtyData,
  validateScheduleInclude,
  createConsultation,
  fetchAndGroupByDoctor,
  fetchAndGroupByDate,
  getDoctorSchedule,
};

export default scheduleService;
